import { alpha, createTheme } from '@mui/material/styles';

/** App chrome tokens. AST blue is the only accent; slide content keeps its own locked tokens in lib/brand. */
const T = {
  blue: '#005EAD',
  blueHover: '#004C8C',
  blueTint: '#E8F1FA',
  ink: '#101828',
  text2: '#475467',
  text3: '#667085',
  control: '#344054',
  border: '#D0D5DD',
  divider: '#E4E7EC',
  subtle: '#F2F4F7',
  hover: '#F9FAFB',
  canvas: '#F5F6F8',
  error: '#B42318',
  success: '#12B76A',
} as const;

declare module '@mui/material/styles' {
  interface Palette {
    neutral: Palette['primary'];
    chrome: { canvas: string; subtle: string; border: string; tint: string };
  }
  interface PaletteOptions {
    neutral?: PaletteOptions['primary'];
    chrome?: { canvas: string; subtle: string; border: string; tint: string };
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    neutral: true;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    neutral: true;
  }
}

const focusRing = `0 0 0 3px ${alpha(T.blue, 0.2)}`;

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: T.blue, dark: T.blueHover, light: T.blueTint, contrastText: '#FFFFFF' },
    neutral: { main: T.control, dark: T.ink, light: T.subtle, contrastText: '#FFFFFF' },
    error: { main: T.error },
    success: { main: T.success },
    text: { primary: T.ink, secondary: T.text2, disabled: T.text3 },
    background: { default: T.canvas, paper: '#FFFFFF' },
    divider: T.divider,
    action: { hover: T.subtle, selected: T.blueTint },
    chrome: { canvas: T.canvas, subtle: T.subtle, border: T.border, tint: T.blueTint },
  },
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
    h6: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.3 },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 600 },
    subtitle2: { fontSize: '0.875rem', fontWeight: 600 },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.8125rem' },
    caption: { fontSize: '0.75rem', lineHeight: 1.4, color: T.text2 },
    overline: { fontSize: '0.75rem', fontWeight: 600, letterSpacing: 0, textTransform: 'none', lineHeight: 1.5 },
    button: { fontSize: '0.8125rem', fontWeight: 500, textTransform: 'none', letterSpacing: 0 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { WebkitFontSmoothing: 'antialiased' },
        '::-webkit-scrollbar': { width: 8, height: 8 },
        '::-webkit-scrollbar-thumb': { background: T.border, borderRadius: 999 },
      },
    },
    MuiButtonBase: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: { '&.Mui-focusVisible': { boxShadow: focusRing } },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          height: 36,
          paddingInline: 14,
          whiteSpace: 'nowrap',
          variants: [
            { props: { variant: 'contained' }, style: { fontWeight: 600 } },
            {
              props: { variant: 'outlined', color: 'neutral' },
              style: {
                borderColor: T.border,
                color: T.control,
                backgroundColor: '#FFFFFF',
                '&:hover': { borderColor: T.border, backgroundColor: T.hover },
              },
            },
            {
              props: { variant: 'text', color: 'neutral' },
              style: { color: T.control, '&:hover': { backgroundColor: T.subtle } },
            },
            {
              props: { variant: 'outlined', color: 'error' },
              style: { borderColor: '#FDA29B', '&:hover': { backgroundColor: '#FEF3F2' } },
            },
          ],
        },
        sizeSmall: { height: 32, paddingInline: 10 },
        startIcon: { marginRight: 6, '& > *:nth-of-type(1)': { fontSize: 18 } },
        endIcon: { marginLeft: 4 },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 8, color: T.text2, '&:hover': { backgroundColor: T.subtle } },
        sizeSmall: { width: 32, height: 32 },
        sizeMedium: { width: 36, height: 36 },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: false, enterDelay: 400 },
      styleOverrides: {
        tooltip: { backgroundColor: T.ink, fontSize: '0.75rem', fontWeight: 500, padding: '6px 8px' },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit', position: 'static' },
      styleOverrides: {
        root: { backgroundColor: '#FFFFFF', borderBottom: `1px solid ${T.divider}` },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 10 },
        outlined: { borderColor: T.divider },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          marginTop: 4,
          border: `1px solid ${T.divider}`,
          boxShadow: '0 8px 24px -4px rgba(16,24,40,0.14)',
        },
        list: { padding: 4 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          borderRadius: 6,
          minHeight: 36,
          '&.Mui-selected': { backgroundColor: T.blueTint },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 30, color: T.text2 } },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: { fontSize: '0.8125rem', fontWeight: 500 },
        secondary: { fontSize: '0.75rem' },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          backgroundColor: '#FFFFFF',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: T.border },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#98A2B3' },
          '&.Mui-focused': { boxShadow: focusRing },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.blue, borderWidth: 1 },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontSize: '0.8125rem' } },
    },
    MuiFormLabel: {
      styleOverrides: { root: { fontSize: '0.75rem', fontWeight: 600, color: T.control } },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiSelect: {
      defaultProps: { size: 'small' },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: { backgroundColor: T.subtle, padding: 3, borderRadius: 8, gap: 2 },
        grouped: { border: 0, borderRadius: '6px !important', margin: 0 },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          height: 30,
          border: 0,
          textTransform: 'none',
          fontSize: '0.8125rem',
          fontWeight: 500,
          color: T.text2,
          '&:hover': { backgroundColor: 'transparent', color: T.ink },
          '&.Mui-selected': {
            color: T.ink,
            fontWeight: 600,
            backgroundColor: '#FFFFFF',
            boxShadow: '0 1px 2px rgba(16,24,40,0.08)',
            '&:hover': { backgroundColor: '#FFFFFF' },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 12, boxShadow: '0 20px 48px -12px rgba(16,24,40,0.3)' },
      },
    },
    MuiBackdrop: {
      styleOverrides: { root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: alpha(T.ink, 0.45) } } },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { padding: '24px 24px 0' } },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: '20px 24px', '.MuiDialogTitle-root + &': { paddingTop: 20 } },
      },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: '16px 24px', borderTop: `1px solid ${T.divider}`, gap: 8 } },
    },
    MuiSnackbarContent: {
      styleOverrides: { root: { backgroundColor: T.ink, borderRadius: 8, fontSize: '0.8125rem' } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 8, fontSize: '0.8125rem' } },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: T.divider } },
    },
  },
});
